/**
 * Field — view/edit/create-aware form cell.
 *
 * Single wrapper that renders the label + value identically in all modes.
 * In view mode: the "value" renders as a bordered read-only block so the
 * layout matches the edit/create modes exactly (no shifting when mode flips).
 * In edit and create modes: the children (inputs/selects) are rendered as-is.
 */

import type { ReactNode } from 'react';
import type { PropertyMode } from './types';

interface FieldProps {
  label: string;
  mode: PropertyMode;
  viewValue: ReactNode;
  children: ReactNode; // the editable input/select for edit mode
  required?: boolean;
  style?: React.CSSProperties;
}

export default function Field({ label, mode, viewValue, children, required, style }: FieldProps) {
  return (
    <div className="form-group" style={style}>
      <label className="form-label">
        {label}
        {required && mode !== 'view' && <span className="form-required"> *</span>}
      </label>
      {mode !== 'view' ? (
        children
      ) : (
        <div
          style={{
            padding: '0.5rem 0.75rem',
            background: 'var(--color-bg, #f9fafb)',
            border: '1px solid var(--color-border, #e5e7eb)',
            borderRadius: 'var(--radius-md)',
            minHeight: '2.25rem',
            display: 'flex',
            alignItems: 'center',
            color: viewValue ? 'var(--color-text, #111827)' : 'var(--color-text-light, #9ca3af)',
            fontSize: 'var(--text-lg)',
          }}
        >
          {viewValue || '—'}
        </div>
      )}
    </div>
  );
}
